import { makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import axios from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import { BillingApi, PriceInfoStore } from 'entities/Billing'
import type { IParamsAddNumberLicenseErrorResponse } from 'entities/Billing/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageNumbersModal } from 'pages/settings/pages/numbers/ui/ManageNumbers/ui/ManageNumbersModal/ManageNumbersModal'
import { RemoveNumbersModal } from 'pages/settings/pages/numbers/ui/ManageNumbers/ui/RemoveNumbersModal/RemoveNumbersModal'
import {
  type IManageNumbersConfig,
  type IManageNumberModalType,
} from 'pages/settings/pages/numbers/ui/ManageNumbers/store/type'

const ERROR_INDICATION_TIME = 5000

export class ManageNumbersStore {
  private _modalId = ''
  private _changeNumberStatus = new RequestLoadData()
  private _addLicenseNumber = 1
  private _disposeAddLicenseNumber: (() => void) | null = null
  private _removeLicenseNumber = 1
  private _priceInfoStore: PriceInfoStore
  private _typeManageNumberModal: IManageNumberModalType = 'default'

  constructor(
    public needLicense = 0,
    private onSuccessBuy = () => {},
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._addLicenseNumber = needLicense || 1

    this._priceInfoStore = new PriceInfoStore((count: number, cancelToken) =>
      BillingApi.getBillingPreview(
        {
          plan: this._entities.subscriptionStore.plan?.shared_inboxes_plan_id || '',
          numbers: Number.isNaN(count) ? 1 : count,
          use_unassigned_numbers: 0,
          use_bought_numbers: 0,
        },
        {
          cancelToken: cancelToken,
        }
      )
    )
    this._reactionChange()
  }

  private _reactionChange = () => {
    this._disposeAddLicenseNumber?.()
    this._disposeAddLicenseNumber = reaction(
      () => this._addLicenseNumber,
      (licenseCount) => this._priceInfoStore.requestInfoStatus.debounceLoadData(licenseCount)
    )
  }

  loadPriceData = (licenseCount: number) => {
    this._priceInfoStore.loadPriceData(licenseCount)
  }

  private _resetManageNumbersState = () => {
    this._modalId = ''
    this._addLicenseNumber = 1
    this._removeLicenseNumber = 1
    this._changeNumberStatus.dispose()
    this._priceInfoStore.reset()
  }

  onManageNumbersModalClose = () => {
    this._disposeAddLicenseNumber?.()
    modalStore.removeModal(this._modalId)
    this._resetManageNumbersState()
  }

  openManageNumbersModal = () => {
    this._modalId = nanoid()
    this._reactionChange()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: this.manageNumbersModalTitle,
      type: ModalTypeList.DEFAULT,
      ModalContent: ManageNumbersModal,
      ModalContentProps: {
        manageNumbersStore: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onManageNumbersModalClose,
      showCloseButton: false,
    })
  }

  openRemoveNumbersModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Remove number licenses',
      type: ModalTypeList.DEFAULT,
      ModalContent: RemoveNumbersModal,
      ModalContentProps: {
        manageNumbersStore: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onManageNumbersModalClose,
      showCloseButton: false,
    })
  }

  onManageNumbers = ({ type }: IManageNumbersConfig) => {
    this._typeManageNumberModal = type

    if (this.isGracePeriod) {
      this._addLicenseNumber = this.usedLicensesDiff
    }

    this._priceInfoStore.loadPriceData(this._addLicenseNumber)
    this.openManageNumbersModal()
  }

  onOpenLicenseRemoveModal = () => {
    modalStore.removeModal(this._modalId)
    this.openRemoveNumbersModal()
  }

  onLicenseRemoveBack = () => {
    this._changeNumberStatus.dispose()
    modalStore.removeModal(this._modalId)
    this.openManageNumbersModal()
  }

  onLicenseRemove = () => {
    return this._changeNumberStatus.blockingLoadDataRequest({
      requestCallback: async () => {
        if (Number.isNaN(this._removeLicenseNumber)) {
          this._removeLicenseNumber = 1
        }

        const response = await BillingApi.deleteNumberLicense({
          delete_numbers_count: String(this._removeLicenseNumber),
        })

        if (String(response.status).startsWith('2')) {
          this._entities.subscriptionStore.init()
          showToast({
            type: 'success',
            title: `${this._removeLicenseNumber} number license${
              this._removeLicenseNumber > 1 ? 's' : ''
            } removed`,
          })
          this.onManageNumbersModalClose()
        } else {
          throw Error()
        }
      },
      errorCallback: () => {
        this._changeNumberStatus.setError('Failed to remove licenses. Please try again later.')
        setTimeout(() => this._changeNumberStatus.dispose(), ERROR_INDICATION_TIME)
      },
    })
  }

  onLicenseBuy = () => {
    return this._changeNumberStatus.blockingLoadDataRequest({
      requestCallback: async () => {
        const response = await BillingApi.addNumberLicense({
          add_numbers_count: this.addLicenseNumber,
        })

        if (String(response.status).startsWith('2')) {
          this._entities.subscriptionStore.init()
          if (this._entities.organizationStore.isNumbersGracePeriod) {
            this._entities.organizationStore.updateOrganizationNumbersGracePeriod(false)
            showToast({
              type: 'success',
              title: 'Payment received. Grace period has ended.',
            })
          } else {
            this.onSuccessBuy()
            showToast({
              type: 'success',
              title: `${this._addLicenseNumber} number license${
                this._addLicenseNumber > 1 ? 's' : ''
              } purchased`,
            })
          }

          this.onManageNumbersModalClose()
        } else {
          throw Error()
        }
      },
      errorCallback: (error) => {
        let errorText = 'Something went wrong. Please try again later.'

        if (axios.isAxiosError<IParamsAddNumberLicenseErrorResponse>(error)) {
          if (error.response?.data.error_type === 'payment') {
            errorText = 'Payment failed. Please check your payment method or try again later.'
          }
        }

        this._changeNumberStatus.setError(errorText)
        setTimeout(() => this._changeNumberStatus.dispose(), ERROR_INDICATION_TIME)
      },
    })
  }

  onAddLicenseNumberChange = async (licenseNumber: string) => {
    const count = parseInt(licenseNumber)
    if (count !== this._addLicenseNumber) {
      this._addLicenseNumber = count
    }
  }

  onRemoveLicenseNumberChange = async (licenseNumber: number) => {
    this._removeLicenseNumber = licenseNumber
  }

  get isManageByGracePeriod() {
    return this._typeManageNumberModal === 'manageByGracePeriod'
  }

  get isPayNowByGracePeriod() {
    return this._typeManageNumberModal === 'payNowByGracePeriod'
  }

  get isGracePeriod() {
    return this.isManageByGracePeriod || this.isPayNowByGracePeriod
  }

  get manageNumbersModalTitle() {
    if (this.isPayNowByGracePeriod) {
      return 'Pay now'
    }
    return 'Manage number licenses'
  }

  get disableButton() {
    return this._changeNumberStatus.loading || this.requestInfoStatus.isLoading
  }

  get loadingButton() {
    return this._changeNumberStatus.loading || this.requestInfoStatus.isLoading
  }

  get licensesCount() {
    if (this._entities.subscriptionStore.numbers) {
      return (
        this._entities.subscriptionStore.numbers.total -
        this._entities.subscriptionStore.numbers.used
      )
    }
    return 0
  }

  get usedLicensesDiff() {
    if (this._entities.subscriptionStore.numbers) {
      return (
        this._entities.subscriptionStore.numbers.used -
        this._entities.subscriptionStore.numbers.total
      )
    }
    return 0
  }

  get addLicenseNumber() {
    return Number.isNaN(this._addLicenseNumber) ? '' : String(this._addLicenseNumber)
  }

  get removeLicenseNumber() {
    return this._removeLicenseNumber
  }

  get priceInfoStore() {
    return this._priceInfoStore
  }

  get priceInfoUiData() {
    return this._priceInfoStore.priceInfoUiData
  }

  get requestInfoStatus() {
    return this._priceInfoStore.requestInfoStatus.status
  }

  get changeNumberStatus() {
    return this._changeNumberStatus.status
  }

  get addLicenseNumberCount() {
    return this._addLicenseNumber
  }
}
